import * as collectionsApi from '@/api/collections';
import type { MemoCollection, MemoCollectionCreate, MemoCollectionUpdate } from '@/api/types/memos/collections';
import type { ApiResponse } from '@/types/api';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useCollectionsStore = defineStore('collections', () => {
  // ===============================
  // État (state)
  // ===============================
  const collections = ref<MemoCollection[]>([]);
  const currentCollection = ref<MemoCollection | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // ===============================
  // Getters (computed)
  // ===============================
  const isLoading = computed(() => loading.value);
  const getError = computed(() => error.value);

  // ===============================
  // Actions
  // ===============================

  /**
   * Enveloppe une action avec la gestion de chargement et d'erreurs.
   */
  async function handleAction<T>(action: Promise<ApiResponse<T>>): Promise<T | null> {
    loading.value = true;
    error.value = null;
    try {
      const response = await action;
      if (response.success) {
        return response.data ?? null;
      }
      throw new Error(response.message || 'An unknown error occurred');
    } catch (e: any) {
      error.value = e.message;
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Charge toutes les collections accessibles (utilisateur + publiques).
   */
  async function fetchCollections() {
    try {
      const accessibleCollections = await collectionsApi.collections.getAccessible();
      collections.value = accessibleCollections;
    } catch (error: any) {
      console.error('❌ useCollectionsStore: Error loading collections', error);
      console.error('❌ useCollectionsStore: Error details', error?.message, error?.response);
      // Fallback vers l'ancien endpoint si le nouveau ne fonctionne pas
      const data = await handleAction(collectionsApi.collections.getMyCollections());
      if (data) {
        collections.value = data;
      }
    }
  }

  /**
   * Charge une collection par son UUID.
   * @param uuid - L'UUID de la collection.
   */
  async function fetchCollection(uuid: string) {
    const data = await handleAction(collectionsApi.collections.getCollection(uuid));
    currentCollection.value = data;
    return data;
  }

  /**
   * Crée une nouvelle collection.
   * @param data - Les données de la nouvelle collection.
   */
  async function createCollection(data: MemoCollectionCreate) {
    const newCollection = await handleAction(collectionsApi.collections.createCollection(data));
    if (newCollection) {
      collections.value.push(newCollection);
    }
    return newCollection;
  }

  /**
   * Met à jour une collection.
   * @param uuid - L'UUID de la collection à mettre à jour.
   * @param data - Les nouvelles données.
   */
  async function updateCollection(uuid: string, data: MemoCollectionUpdate) {
    const updatedCollection = await handleAction(collectionsApi.collections.updateCollection(uuid, data));
    if (updatedCollection) {
      const index = collections.value.findIndex(c => c.uuid === uuid);
      if (index !== -1) {
        collections.value[index] = updatedCollection;
      }
      if (currentCollection.value?.uuid === uuid) {
        currentCollection.value = updatedCollection;
      }
    }
    return updatedCollection;
  }

  /**
   * Supprime une collection.
   * @param uuid - L'UUID de la collection à supprimer.
   */
  async function deleteCollection(uuid: string) {
    try {
      const response = await collectionsApi.collections.deleteCollection(uuid);
      if (response.success) {
        collections.value = collections.value.filter(c => c.uuid !== uuid);
        if (currentCollection.value?.uuid === uuid) {
          currentCollection.value = null;
        }
        return true;
      }
      error.value = response.message || 'Erreur lors de la suppression';
      return false;
    } catch (e: any) {
      error.value = e.message;
      return false;
    }
  }

  /**
   * Ajoute un mémo à une collection.
   */
  async function addMemoToCollection(collectionUuid: string, memoUuid: string) {
    // Ne pas utiliser handleAction ici car on veut propager les erreurs (409, etc.)
    // pour que Promise.allSettled puisse les détecter
    const response = await collectionsApi.collections.addMemoToCollection(collectionUuid, { memo_uuid: memoUuid });
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to add memo to collection');
    }
    return response.data;
  }

  /**
   * Retire un mémo d'une collection.
   */
  async function removeMemoFromCollection(collectionUuid: string, memoUuid: string) {
    try {
      const response = await collectionsApi.collections.removeMemoFromCollection(collectionUuid, memoUuid);
      if (response.success) {
        await fetchCollections(); // Mettre à jour les compteurs dans la liste des collections
        return true;
      }
      error.value = response.message || 'Erreur lors de la suppression du mémo';
      return false;
    } catch (e: any) {
      error.value = e.message;
      return false;
    }
  }
  
  /**
   * Réinitialise l'erreur.
   */
  function clearError() {
    error.value = null;
  }

  /**
   * Ajoute plusieurs mémos à une collection en une seule fois.
   * @param collectionUuid - L'UUID de la collection.
   * @param memoUuids - Un tableau d'UUIDs de mémos.
   */
  async function addMemosToCollection(collectionUuid: string, memoUuids: string[]) {
    loading.value = true;
    error.value = null;
    try {
      const promises = memoUuids.map(memoUuid =>
        collectionsApi.collections.addMemoToCollection(collectionUuid, { memo_uuid: memoUuid })
      );
      await Promise.all(promises);
      // Re-fetch pour s'assurer que les données sont à jour
      await fetchCollection(collectionUuid);
      await fetchCollections(); // Met à jour la liste des collections (pour le compteur)
    } catch (e: any) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    collections,
    currentCollection,
    // Getters
    isLoading,
    getError,
    // Actions
    fetchCollections,
    fetchCollection,
    createCollection,
    updateCollection,
    deleteCollection,
    addMemoToCollection,
    removeMemoFromCollection,
    clearError,
    addMemosToCollection,
  };
}); 